'use client'

import * as SwitchPrimitives from '@radix-ui/react-switch'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '@/lib/utils'
import { ApplicationOrientation } from '~/common/generated/apollo-operations'
import { useApplicationOrientation } from '~/common/hooks/useApplicationOrientation'

export interface SwitchProps
  extends
    React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
    VariantProps<typeof switchVariants> {}

const switchVariants = cva(
  'peer inline-flex h-4 w-[30px] shrink-0 items-center rounded-full border-2 border-transparent transition-colors disabled:data-[state=checked]:bg-gray-4 disabled:data-[state=unchecked]:bg-gray-4 hover:data-[state=unchecked]:bg-gray-7 focus-visible:outline-none focus-visible:shadow-[0_0_0_1px_transparent,0_0_0_2px_theme(colors.doss.focus)]',
  {
    variants: {
      variant: {
        default: 'data-[state=checked]:bg-blue-9 data-[state=unchecked]:bg-gray-5',
      },
      size: {
        default: 'h-4 w-[30px]',
        lg: 'h-5 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

const Switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, SwitchProps>(
  ({ className, size, variant, ...props }, ref) => {
    const { orientation } = useApplicationOrientation()

    const thumbSize = size === 'lg' ? 'h-4 w-4' : 'h-[11px] w-[11px]'
    let thumbPosition = ''

    if (orientation === ApplicationOrientation.RIGHT_TO_LEFT) {
      thumbPosition =
        size === 'lg'
          ? 'data-[state=checked]:-translate-x-4'
          : 'data-[state=checked]:-translate-x-[15px]'
    } else {
      thumbPosition =
        size === 'lg'
          ? 'data-[state=checked]:translate-x-4'
          : 'data-[state=checked]:translate-x-[15px]'
    }

    return (
      <SwitchPrimitives.Root
        className={cn(switchVariants({ variant, size }), className)}
        {...props}
        ref={ref}
      >
        <SwitchPrimitives.Thumb
          className={cn(
            'pointer-events-none block rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=unchecked]:translate-x-0',
            thumbSize,
            thumbPosition,
          )}
        />
      </SwitchPrimitives.Root>
    )
  },
)
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
